import type { FileCommentMessageDatagramBuf, FileCommentResponseDatagramBuf } from "~/classes/generated/datagram"

import { StaticFile } from "~/classes/file/StaticFile"
import { DatagramBuf, FileCommentRequestDatagramBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { addFileToBuf, parseFile } from "~/utils/flatbuffers/file"
import { parseFileId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("file/comment")

export function parseFileCommentRequestDatagramBuf(
  datagram: FileCommentRequestDatagramBuf,
): FileCommentRequestDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    file: parseFile(datagram.file()!),
    offline: datagram.offline(),
  }
}

export function sendFileCommentRequests(requests: FileCommentRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)

  const requestVector = []
  for (const request of requests) {
    const file = addFileToBuf(builder, request.file)
    const requestId = addRequestIdToBuf(builder, request.requestId)
    FileCommentRequestDatagramBuf.startFileCommentRequestDatagramBuf(builder)
    FileCommentRequestDatagramBuf.addRequestId(builder, requestId)
    FileCommentRequestDatagramBuf.addFile(builder, file)
    FileCommentRequestDatagramBuf.addOffline(builder, request.offline)
    const commentRequest = FileCommentRequestDatagramBuf.endFileCommentRequestDatagramBuf(builder)

    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addFileCommentRequest(builder, commentRequest)
    requestVector.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const vector = DatagramBuf.createRequestVector(builder, requestVector)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, vector)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

export function parseFileCommentResponseDatagramBuf(
  datagram: FileCommentResponseDatagramBuf,
): FileCommentResponseDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    file: maybe(parseFile)(datagram.file()),
    fileId: parseFileId(datagram.fileId()!),
    offline: datagram.offline(),
  }
}

export function handleFileCommentResponseDatagramBuf(datagram: FileCommentResponseDatagramBuf) {
  onFileCommentResponse(parseFileCommentResponseDatagramBuf(datagram))
}

export function handleFileCommentMessageDatagramBuf(datagram: FileCommentMessageDatagramBuf) {
  onFileCommentMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    file: parseFile(datagram.file()!),
  })
}

export function onFileCommentResponse(response: FileCommentResponseDatagram, quiet = false) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  emitInterfaceEvent("fileCommentResponse", response, response.requestId)

  if (response.error)
    return onFileCommentErrored(response.error, quiet)
  if (!response.file)
    throw new Error("No file in comment response")

  const iFile = new StaticFile(response.file)
  emitEntityEvent("fileProperties", iFile.id, iFile.inner(), response.offline)

  // TODO: we skip the inferred parent id here since the response doesn't include
  // the path
}

export function onFileCommentMessage(response: FileCommentMessageDatagram) {
  const { emitEntityEvent } = useEvents()

  const iFile = new StaticFile(response.file)
  emitEntityEvent("fileProperties", iFile.id, iFile.inner(), false)

  if (iFile.isRootFolder())
    return

  const parentId = iFile.inferredParentId()
  if (!parentId) {
    console.error("No parent id in file comment response", response)
    throw new Error("no parent id in file comment response")
  }
  emitEntityEvent("childProperties", parentId, iFile.inner(), false)
}

export function onFileCommentErrored(
  error: DatagramError,
  quiet = false,
) {
  const { notifyDatagramError } = useAlerts()

  console.error(error)

  if (!quiet)
    notifyDatagramError(error, "File comment was unsuccessful")
}
